import React, { useState, useEffect, Fragment } from 'react';
import { connect } from 'react-redux';
import { Button, Modal, Menu, Form, Input, Checkbox } from 'antd';
import {
	CameraOutlined,
	FileTextOutlined,
	FolderOutlined,
	LinkOutlined,
	PictureOutlined,
	TabletOutlined,
	TrophyOutlined,
	UploadOutlined,
	VideoCameraOutlined,
} from '@ant-design/icons';
import { Microphone } from '@blueprintnyc/bpnyc-common-admin/dist/icons';
import { HiOutlineSwitchHorizontal } from 'react-icons/hi';
import Select from 'components/Select';
import MediaUploadDialog from 'components/MediaUploadDialog';
import PagePicker from './PagePicker';
import MediaLibraryPicker from './MediaLibraryPicker';
import BroadcastPicker from './BroadcastPicker';
import PhotoboothPicker from './PhotoboothPicker';
import AIPhotoboothPicker from './AIPhotoboothPicker';
import LeaderboardPicker from './LeaderboardPicker';
import { isSaaSAdmin } from '@/redux/util';
import EventPicker from './EventPicker';
import MaterialsPicker from './MaterialsPicker';
import PodcastsPicker from './PodcastsPicker';

import './OmniPicker.scss';

const FormItem = Form.Item;

const OmniPicker = props => {
	const {
		visible,
		onCancel,
		event,
		mediaPicker,
		linkPicker,
		handlePickItem: propsPickItem,
		handlePickMedia,
		currentClient,
		auth0Token,
		from,
		onPress: propsOnPress,
	} = props;
	const isSaaS = isSaaSAdmin();

	const [tab, setTab] = useState('');
	const [onPress, setOnPress] = useState({});
	const [form] = Form.useForm();
	const { route, link, eventID } = onPress;

	const isSinglePageApp = event.single_page_app === 'TRUE';
	const possibleTabs = [
		'pageStack',
		'agendaItemStack',
		'mediaStack',
		'libraryStack',
		'mediaUploadStack',
		'broadcasts',
		'photobooth',
		'ai-photobooth',
		'leaderboard',
		'podcasts',
	];
	const routeStr = route && route.split('/')[1];
	const appRouteValue = possibleTabs.includes(routeStr) ? null : routeStr;

	useEffect(() => {
		// validate onPress below, can be removed in the future (transitioning onPress to obj)
		if (typeof propsOnPress === 'string' && propsOnPress.startsWith('/'))
			setOnPress({ route: propsOnPress });
		else if (propsOnPress) setOnPress(propsOnPress);
		else setOnPress({});
	}, [propsOnPress]);

	useEffect(() => {
		if (!onPress) setTab('');
		else {
			if (mediaPicker) setTab('mediaStack');
			else setTabState();
		}
		// below necessary for antd form to update initialValues
		form.setFieldsValue(onPress);
	}, [onPress, mediaPicker]);

	const setTabState = () => {
		if (link) setTab('URL');
		else if (route) possibleTabs.includes(routeStr) ? setTab(routeStr) : setTab('appRoutes');
		else if (eventID) setTab('switchEvent');
		else setTab('');
	};

	const handlePickItem = item => e => {
		if (e && e.preventDefault) e.preventDefault();
		const routeName =
			tab === 'materials' ? 'mediaStack' : tab === 'materialFolders' ? 'libraryStack' : tab;
		propsPickItem({ route: `/${routeName}/${item.id}`, name: item.name });
	};

	const handlePickLink = values => {
		const { link, openInBrowser, name } = values;
		propsPickItem({
			link: link.trim(),
			openInBrowser,
			name,
		});
	};

	const handleSetAppRoute = value => {
		if (value === 'KL') {
			const isProd = import.meta.env.MODE === 'production';
			const configURL = isProd ? 'https://kudos.bpdev.club/' : 'http://localhost:3004';
			handlePickLink({ link: configURL, openInBrowser: false });
		} else {
			propsPickItem({ route: `/${value}` });
		}
	};

	const appRouteOpts = [
		{ id: 'home', name: 'Home' },
		{ id: 'announcements', name: 'Announcements' },
		{ id: 'agenda', name: 'Agenda' },
		{ id: 'directory', name: 'Directory' },
		{ id: 'materials', name: 'Materials' },
		{ id: 'feed', name: 'Social Feed' },
		{ id: 'notifications', name: 'Notifications' },
		{ id: 'scanQR', name: 'Scan QR' },
		{ id: 'attendeeDetail', name: 'Profile' },
		{ id: 'settings', name: 'Settings' },
		{ id: 'podcasts', name: 'Podcasts' },
	];

	if (!isSaaS) {
		appRouteOpts.push({ id: 'KL', name: 'Kudos Leaderboard' });
		appRouteOpts.push({ id: 'pointsForm', name: 'Points Form' });
	}

	return (
		<Modal title="Select Item" open={visible} footer={null} width={960} onCancel={onCancel}>
			<Menu
				onClick={({ key }) => setTab(key)}
				selectedKeys={[tab]}
				mode="horizontal"
				className="omniPickerMenuHeader"
			>
				{!mediaPicker && !linkPicker ? (
					<Fragment>
						{!isSinglePageApp ? (
							<Fragment>
								{from !== 'navigation' && (
									<Menu.Item key="appRoutes">
										<TabletOutlined />
										App Routes
									</Menu.Item>
								)}
								<Menu.Item key="pageStack">
									<FileTextOutlined />
									Pages
								</Menu.Item>
							</Fragment>
						) : null}
						<Menu.Item key="URL">
							<LinkOutlined />
							URL
						</Menu.Item>
						{from !== 'checkIns' ? (
							<Menu.Item key="broadcast">
								<VideoCameraOutlined />
								Broadcast
							</Menu.Item>
						) : null}
						<Menu.Item key="photobooth">
							<CameraOutlined />
							Photo Booth
						</Menu.Item>
						<Menu.Item key="aiPhotobooth">
							<CameraOutlined />
							AI Photo Booth
						</Menu.Item>
						<Menu.Item key="leaderboard">
							<TrophyOutlined />
							Leaderboard
						</Menu.Item>
						<Menu.Item key="podcasts">
							<Microphone />
							Podcasts
						</Menu.Item>
						{!isSaaS ? (
							<Menu.Item key="switchEvent">
								<HiOutlineSwitchHorizontal />
								Event
							</Menu.Item>
						) : null}
					</Fragment>
				) : null}
				{linkPicker && (
					<Fragment>
						<Menu.Item key="URL">
							<LinkOutlined />
							URL
						</Menu.Item>
						<Menu.Item key="broadcast">
							<VideoCameraOutlined />
							Broadcast
						</Menu.Item>
						<Menu.Item key="appRoutes">
							<TabletOutlined />
							App Routes
						</Menu.Item>
						<Menu.Item key="pageStack">
							<FileTextOutlined />
							Pages
						</Menu.Item>
					</Fragment>
				)}
				{!linkPicker && (
					<Fragment>
						<Menu.Item key="materials">
							<PictureOutlined />
							Materials
						</Menu.Item>
						<Menu.Item key="materialFolders">
							<FolderOutlined />
							Material Folders
						</Menu.Item>
						<Menu.Item key="mediaUploadStack">
							<UploadOutlined />
							Media Upload
						</Menu.Item>
						{!isSaaS ? (
							<>
								<Menu.Item key="libraryStack">
									<FolderOutlined />
									Libraries
								</Menu.Item>
								<Menu.Item key="mediaStack">
									<PictureOutlined />
									Media
								</Menu.Item>
							</>
						) : null}
					</Fragment>
				)}
			</Menu>

			<div className="omniPickerBody" style={{ marginTop: '1em' }}>
				{
					{
						pageStack: <PagePicker eventID={event.id} handlePickItem={handlePickItem} />,
						mediaStack: (
							<MediaLibraryPicker
								event={event}
								client={currentClient}
								handlePickItem={
									mediaPicker ? media => handlePickMedia(media, from) : handlePickItem
								}
								stack="media"
								mediaPicker={mediaPicker}
							/>
						),

						mediaUploadStack: (
							<MediaUploadDialog
								stack={true}
								event={event}
								client={currentClient}
								handlePickItem={handlePickMedia}
								from={from}
							/>
						),
						libraryStack: (
							<MediaLibraryPicker
								event={event}
								client={currentClient}
								handlePickItem={mediaPicker ? handlePickMedia : handlePickItem}
								stack="library"
								mediaPicker={mediaPicker}
							/>
						),
						materials: (
							<MaterialsPicker
								event={event}
								handlePickItem={mediaPicker ? handlePickMedia : handlePickItem}
								from={'materialsPicker'}
							/>
						),
						materialFolders: (
							<MaterialsPicker
								event={event}
								handlePickItem={mediaPicker ? handlePickMedia : handlePickItem}
								from={'materialFoldersPicker'}
							/>
						),
						broadcast: (
							<BroadcastPicker
								auth0Token={auth0Token}
								event={event}
								handlePickItem={propsPickItem}
								broadcast={route}
							/>
						),
						photobooth: (
							<PhotoboothPicker
								auth0Token={auth0Token}
								event={event}
								photobooth={route}
								handlePickItem={propsPickItem}
							/>
						),
						aiPhotobooth: (
							<AIPhotoboothPicker aiPhotobooth={route} handlePickItem={propsPickItem} />
						),
						leaderboard: <LeaderboardPicker leaderboard={route} handlePickItem={propsPickItem} />,
						switchEvent: <EventPicker onPress={onPress} handlePickItem={propsPickItem} />,
						appRoutes: (
							<div>
								<Select
									data={appRouteOpts}
									placeholder="Select an app route"
									value={appRouteValue ? [appRouteValue] : []}
									onChange={handleSetAppRoute}
								/>
							</div>
						),
						URL: (
							<Fragment>
								<Form form={form} onFinish={handlePickLink} initialValues={onPress}>
									<div style={{ display: 'flex' }}>
										{from === 'navigation' && (
											<FormItem label="Display Name" name="name" style={{ marginRight: '1em' }}>
												<Input />
											</FormItem>
										)}
										<FormItem label="Custom URL" name="link" style={{ width: '100%' }}>
											<Input />
										</FormItem>
									</div>
									<FormItem
										label="Open in new browser?"
										name="openInBrowser"
										valuePropName="checked"
									>
										<Checkbox />
									</FormItem>
									<Button type="primary" htmlType="submit">
										Save URL
									</Button>
								</Form>
							</Fragment>
						),
						podcasts: <PodcastsPicker event={event} handlePickItem={propsPickItem} route={route} />,
					}[tab]
				}
			</div>
		</Modal>
	);
};

const msp = ({ clients, auth }) => ({
	currentClient: clients.currentClient,
	auth0Token: auth.auth0Token,
});

export default connect(msp, null)(OmniPicker);
